import { useState } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Send, Pause, Play, XCircle, Trash2, FlaskConical, Loader2, Pencil, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useBlastCampaign, useBlastMutations, useBlastMailboxes, useComposeMeta, type BlastRecipient, type BlastCampaign } from "@/hooks/useBlast";

const STATUS_STYLE: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  scheduled: "bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
  sending: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800",
  paused: "bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800",
  completed: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  cancelled: "bg-red-100 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-900",
};

const DAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TZ_ABBR: Record<string, string> = {
  "America/New_York": "ET", "America/Chicago": "CT", "America/Denver": "MT",
  "America/Phoenix": "MST", "America/Los_Angeles": "PT",
};
function scheduleSummary(c: { send_window_start: number; send_window_end: number; send_days: number[] | null; timezone: string | null; pacing: string | null }): string {
  const days = c.send_days && c.send_days.length ? c.send_days : [1, 2, 3, 4, 5];
  const dayStr = days.length === 7 ? "every day"
    : days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d)) ? "weekdays"
    : days.map((d) => DAY_ABBR[d]).join("/");
  const hr = (h: number) => h === 0 ? "12am" : h === 24 ? "12am" : h === 12 ? "12pm" : h < 12 ? `${h}am` : `${h - 12}pm`;
  const tz = TZ_ABBR[c.timezone || "America/Chicago"] || "CT";
  return `${dayStr} ${hr(c.send_window_start)}–${hr(c.send_window_end)} ${tz}${c.pacing === "fast" ? ", fast" : ""}`;
}

const RECIPIENT_STYLE: Record<BlastRecipient["status"], string> = {
  pending: "text-muted-foreground",
  sent: "text-green-700 dark:text-green-400",
  replied: "text-blue-700 font-medium dark:text-blue-400",
  failed: "text-red-600 dark:text-red-400",
  skipped: "text-muted-foreground/60",
  bounced: "text-red-700 font-medium dark:text-red-400",
};

// Renders the email a recipient actually got (or their pending draft):
// their personalized copy when there is one, otherwise the campaign
// template with merge fields resolved, plus signature and disclosure.
// Matches the server's send-path escaper (services/blast.ts) so this view
// renders exactly what goes out. Quotes included: merge values come from
// imported spreadsheets, so they must be inert in attribute context too.
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
function mergeFields(t: string, r: BlastRecipient, escape: boolean): string {
  const firstName = (r.name || "").split(/\s+/)[0] || "there";
  const v = (s: string) => (escape ? escapeHtml(s) : s);
  return t
    .replace(/\{\{\s*first_name\s*\}\}/gi, v(firstName))
    .replace(/\{\{\s*full_name\s*\}\}/gi, v(r.name || "there"))
    .replace(/\{\{\s*company_name\s*\}\}/gi, v(r.company_name || "your firm"));
}

function RecipientEmailDialog({
  recipient, campaign, onClose,
}: { recipient: BlastRecipient | null; campaign: BlastCampaign; onClose: () => void }) {
  const { data: meta } = useComposeMeta();
  const { data: mailboxes = [] } = useBlastMailboxes();
  if (!recipient) return null;

  const sender = mailboxes.find((mb) => mb.fromAddress === recipient.sent_from)
    ?? mailboxes.find((mb) => mb.isActive) ?? mailboxes[0] ?? null;
  const subject = mergeFields(recipient.custom_subject || campaign.subject, recipient, false);
  const bodyHtml = mergeFields(recipient.custom_body || campaign.body_html, recipient, true);
  const rawSig = sender?.signatureHtml ?? "";
  const sig = rawSig ? `<br><br>${meta ? rawSig.replace(/cid:ftc-logo/g, meta.logoDataUri) : rawSig}` : "";
  const wasSent = !!recipient.sent_at;

  return (
    <Dialog open={!!recipient} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap pr-6">
            <Mail className="w-4 h-4" />
            {wasSent ? "Email sent" : recipient.custom_body ? "Draft, not sent yet" : "Will send this"}
            <span className={cn("text-xs font-normal", RECIPIENT_STYLE[recipient.status])}>{recipient.status}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 min-w-0">
          {recipient.error && (
            <p className="text-xs text-red-600 border border-red-200 bg-red-50 rounded px-3 py-2 dark:text-red-400 dark:border-red-900 dark:bg-red-950/40">{recipient.error}</p>
          )}
          <div className="border rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b bg-muted/30 text-sm space-y-0.5">
              <p><span className="text-muted-foreground inline-block w-12">From</span>{" "}
                {recipient.sent_from
                  ? `${sender?.displayName || "First Turn Capital"} <${recipient.sent_from}>`
                  : "rotates when it sends"}</p>
              <p><span className="text-muted-foreground inline-block w-12">To</span>{" "}
                {recipient.name ? `${recipient.name} <${recipient.email}>` : recipient.email}</p>
              {recipient.sent_at && (
                <p><span className="text-muted-foreground inline-block w-12">Sent</span> {new Date(recipient.sent_at).toLocaleString()}</p>
              )}
              <p className="font-medium pt-0.5">{subject || "(no subject)"}</p>
            </div>
            <div className="px-5 py-4 text-sm max-h-[440px] overflow-y-auto bg-white text-neutral-900 dark:text-neutral-200"
              dangerouslySetInnerHTML={{ __html: `${bodyHtml}${sig}${meta?.complianceFooterHtml ?? ""}` }} />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CampaignDetail() {
  const [, params] = useRoute("/campaigns/:id");
  const [, setLocation] = useLocation();
  const id = params?.id ?? null;
  const { data: campaign, isLoading } = useBlastCampaign(id);
  const m = useBlastMutations();
  const [filter, setFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [viewing, setViewing] = useState<BlastRecipient | null>(null);

  if (isLoading || !campaign) {
    return (
      <AppLayout title="Campaign">
        <div className="space-y-4"><Skeleton className="h-10 w-72" /><Skeleton className="h-52" /></div>
      </AppLayout>
    );
  }

  const counts = campaign.recipients.reduce<Record<string, number>>((acc, r) => {
    acc[r.status] = (acc[r.status] ?? 0) + 1;
    return acc;
  }, {});
  const total = campaign.recipients.length;
  const done = (counts.sent ?? 0) + (counts.replied ?? 0) + (counts.failed ?? 0) + (counts.skipped ?? 0);
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;
  const q = search.trim().toLowerCase();
  const visible = campaign.recipients.filter((r) =>
    (filter === "all" || r.status === filter) &&
    (!q || `${r.name ?? ""} ${r.email} ${r.company_name ?? ""}`.toLowerCase().includes(q)),
  );

  const fmtSentAt = (iso: string) => {
    const d = new Date(iso);
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay
      ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
      : d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const act = async (action: "launch" | "pause" | "resume" | "cancel") => {
    try {
      await m.lifecycle.mutateAsync({ id: campaign.id, action });
      toast.success(`Campaign ${action === "launch" ? "launched" : action + "d"}`);
    } catch (e: any) { toast.error(e.message); }
  };

  return (
    <AppLayout title={campaign.name}>
      <div className="space-y-4 page-enter">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <Link href="/campaigns" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-1">
              <ArrowLeft className="w-3 h-3" /> Campaigns
            </Link>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 flex-wrap">
              {campaign.name}
              <Badge variant="outline" className={cn("text-[10px]", STATUS_STYLE[campaign.status])}>{campaign.status}</Badge>
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {campaign.campaign_type.toUpperCase()}
              {campaign.client_name && <> · <Link href={`/clients/${campaign.client_id}`} className="text-primary-text hover:underline">{campaign.client_name}</Link></>}
              {" "}· {scheduleSummary(campaign)}
              {campaign.status === "scheduled" && campaign.scheduled_start_at && (
                <span className="text-indigo-600 dark:text-indigo-400"> · starts {new Date(campaign.scheduled_start_at).toLocaleString("en-US", { timeZone: campaign.timezone || "America/Chicago", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} {TZ_ABBR[campaign.timezone || "America/Chicago"]}</span>
              )}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" className="gap-1" disabled={m.testSend.isPending}
              onClick={async () => {
                try { const r = await m.testSend.mutateAsync(campaign.id); toast.success(`Test sent to ${r.sentTo}`); }
                catch (e: any) { toast.error(e.message); }
              }}>
              {m.testSend.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />} Test send
            </Button>
            {campaign.status === "draft" && (
              <Link href={`/campaigns/${campaign.id}/edit`}>
                <Button size="sm" variant="outline" className="gap-1"><Pencil className="w-4 h-4" /> Edit</Button>
              </Link>
            )}
            {(campaign.status === "draft" || campaign.status === "paused") && (
              <Button size="sm" className="gap-1" onClick={() => act(campaign.status === "paused" ? "resume" : "launch")}>
                {campaign.status === "paused" ? <Play className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                {campaign.status === "paused" ? "Resume" : "Launch"}
              </Button>
            )}
            {(campaign.status === "sending" || campaign.status === "scheduled") && (
              <Button size="sm" variant="outline" className="gap-1" onClick={() => act("pause")}>
                <Pause className="w-4 h-4" /> {campaign.status === "scheduled" ? "Hold" : "Pause"}
              </Button>
            )}
            {["draft", "scheduled", "sending", "paused"].includes(campaign.status) && (
              <Button size="sm" variant="outline" className="gap-1 text-red-600 dark:text-red-400" onClick={() => setConfirmCancel(true)}>
                <XCircle className="w-4 h-4" /> Cancel
              </Button>
            )}
            {["draft", "cancelled", "completed"].includes(campaign.status) && (
              <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground" onClick={() => setConfirmDelete(true)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Progress */}
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="font-medium">{done} of {total} processed</span>
            <span className="text-muted-foreground">{progress}%</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div className={cn("h-full rounded-full", campaign.status === "sending" ? "bg-green-500" : "bg-primary")} style={{ width: `${progress}%` }} />
          </div>
          <div className="flex gap-4 mt-3 text-xs text-muted-foreground flex-wrap">
            <span>Pending {counts.pending ?? 0}</span>
            <span className="text-green-700 dark:text-green-400">Sent {counts.sent ?? 0}</span>
            <span className="text-blue-700 dark:text-blue-400">Replied {counts.replied ?? 0}</span>
            <span className="text-red-600 dark:text-red-400">Failed {counts.failed ?? 0}</span>
            <span className="text-red-700 dark:text-red-400">Bounced {counts.bounced ?? 0}</span>
            <span>Skipped {counts.skipped ?? 0}</span>
          </div>
        </div>

        {/* Recipients */}
        <div className="bg-card border rounded-lg">
          <div className="flex items-center gap-1.5 px-4 pt-3 pb-2 flex-wrap">
            {["all", "pending", "sent", "replied", "failed", "bounced", "skipped"].map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn("text-xs px-2.5 py-1 rounded-full border transition-colors",
                  filter === f ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:bg-muted/50")}>
                {f === "all" ? `All ${total}` : `${f} ${counts[f] ?? 0}`}
              </button>
            ))}
            <input
              className="ml-auto h-7 w-52 max-w-full rounded-md border border-border bg-transparent px-2.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="Search recipients…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="divide-y max-h-[520px] overflow-y-auto">
            {visible.length === 0 ? (
              <p className="text-sm text-muted-foreground px-4 py-6 text-center">No recipients{filter !== "all" ? ` with status ${filter}` : ""}.</p>
            ) : visible.map((r) => (
              <div key={r.id} onClick={() => setViewing(r)}
                title="View the email for this recipient"
                className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors">
                <div className="min-w-0 flex-1">
                  <span className="font-medium">{r.name || r.email}</span>
                  {r.company_name && (
                    r.company_id
                      ? <Link href={`/buyers/${r.company_id}`} onClick={(e) => e.stopPropagation()} className="text-muted-foreground hover:text-primary-text"> · {r.company_name}</Link>
                      : <span className="text-muted-foreground"> · {r.company_name}</span>
                  )}
                  {r.error && <p className="text-xs text-red-500 truncate">{r.error}</p>}
                </div>
                {r.sent_at && <span className="text-[11px] text-muted-foreground shrink-0 hidden sm:inline tabular-nums">{fmtSentAt(r.sent_at)}</span>}
                {r.sent_from && <span className="text-[11px] text-muted-foreground shrink-0 hidden md:inline">via {r.sent_from}</span>}
                <span className={cn("text-xs shrink-0 w-16 text-right", RECIPIENT_STYLE[r.status])}>{r.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RecipientEmailDialog recipient={viewing} campaign={campaign} onClose={() => setViewing(null)} />

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancel campaign"
        description="Pending recipients will not be emailed. Already-sent emails are unaffected. This cannot be resumed."
        confirmLabel="Cancel campaign"
        variant="destructive"
        onConfirm={async () => { await act("cancel"); setConfirmCancel(false); }}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete campaign"
        description="Removes the campaign and its recipient list. Activity already logged on buyer records stays."
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          try { await m.deleteCampaign.mutateAsync(campaign.id); setLocation("/campaigns"); }
          catch (e: any) { toast.error(e.message); }
        }}
      />
    </AppLayout>
  );
}
